import { Injectable } from '@angular/core';
import { ExportContext, ExportFileFormat, ExportRange, ExportService } from 'app/layout/export-overlay';
import { buildQuery, page, seed } from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { takeUntil } from 'rxjs';
import get from 'lodash-es/get';
import ceil from 'lodash-es/ceil';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { LOCALIZED_PROPERTIES, SUB_ROUTE_PERIODICITY_EXPORT_ID, SUB_ROUTE_PERIODICITY_TITLE } from './sub-route-periodicity.constants';
import EmailAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/email/email.entity';

@Injectable()
export class SubRoutePeriodicityExport {
  constructor(
    private exportService: ExportService,
    private businessService: BusinessPartnerService,
  ) {}

  private transformItem(item: BusinessPartnerAttributes) {
    return {
      externalKeys: get(item, 'externalKeys.code', ''),
      name: get(item, 'name', ''),
      commercialName: get(item, 'commercialName', ''),
      NIT: get(item, 'NIT', ''),
      DNI: get(item, 'DNI', ''),
      subRoutes: this.getSubRoutes(get(item,'subRoutes', [])),
      principalContact: get(item, 'principalContact.fullName', ''),
      principalPhone: `${get(item, 'principalPhone.prefix', '')}-${get(item, 'principalPhone.phone', '')}`,
      emails: this.getEmail(get(item,'principalContact.emails', [])),
      principalAddress: get(item, 'principalAddress.address', ''),
      principalAddressLocation: this.getLocation(item), //addresses locations
      frequenciesFromThisSubRoute: this.getFrequency(item),
      frecuencyFromEvent: this.getEventFrecuency(item),
    };
  }

  private getSubRoutes(subRoutes): string {
    if (!subRoutes || subRoutes.length === 0) {
        return '';
    }

    const subRoutesName = subRoutes.map(subRoute => subRoute.name);
    return subRoutesName.join(', ') || '';
  }

  private getEmail(emails: EmailAttributes[]): string {
      if (!emails || emails.length === 0) {
          return '';
      }
  
      const principalEmail = emails.find(email => email.isPrincipal)?.email;
      return principalEmail || emails[0].email || '';
  }
  
  private getLocation(item: BusinessPartnerAttributes): string {
      const principalAddress = get(item, 'principalAddress', {});
  
      if (!principalAddress || typeof principalAddress !== 'object') {
          return '??';
      }
  
      const locationOneName = get(principalAddress, 'locationOne.name', '').trim();
      const locationTwoName = get(principalAddress, 'locationTwo.name', '').trim();
      const locationThreeName = get(principalAddress, 'locationThree.name', '').trim();
  
      return [locationOneName, locationTwoName, locationThreeName].filter(Boolean).join(' - ') || '??';
  }
  
  private getFrequency(item: BusinessPartnerAttributes): string {
      const frequencies = get(item, 'frequenciesFromThisSubRoute', []);
  
      if (!Array.isArray(frequencies) || frequencies.length === 0) {
          return '';
      }
  
      const daysMap: { [key: number]: string } = {
        1: "Lunes",
        2: "Martes",
        3: "Miércoles",
        4: "Jueves",
        5: "Viernes",
        6: "Sábado",
        7: "Domingo",
      };

      const allDays: number[] = [];
      const allDates: string[] = [];

      for (const freq of frequencies) {
        if (freq.type === "W") {
          allDays.push(...get(freq, "days", []));
        } else if (freq.type === "C") {
          allDates.push(...get(freq, "dates", []));
        }
      }

      if (allDays.length > 0) {
        const uniqueDays = [...new Set(allDays)].sort((a, b) => a - b);
        return uniqueDays
          .map((d) => daysMap[d] || "")
          .filter(Boolean)
          .join(', ');
      }
      return "";
  }

  private getEventFrecuency(item: BusinessPartnerAttributes): number {
    const frequencies = get(item, 'frequenciesFromThisSubRoute', []);
  
    if (!Array.isArray(frequencies) || frequencies.length === 0) {
      return 0;
    }

    const calendar = frequencies.filter(f => f.type === 'C').length;
    return calendar;
  }

  export(id: string, title: string,
    format: ExportFileFormat, range: ExportRange, totalItems: number, pageSize: number, pageIndex: number,idSubRoutePeriodicity?: string, type?: boolean
   ) {
    const totalPages = ceil(totalItems / pageSize) + 1;
    this.exportService.task({
      id: id,
      title: title,
      format: format,
      iterations: range === ExportRange.AllPages ? totalPages : 1,
      start: (ctx: ExportContext) => {
        ctx.processor.localizeItemProperties(title, LOCALIZED_PROPERTIES);
      },
      next: (ctx: ExportContext) => {
        const currentPage = range === ExportRange.AllPages ? ctx.iteration : pageIndex;
        this.businessService.findByFilterTwo(buildQuery(
          seed(this.businessService.currentFilterTwo), page(currentPage, pageSize)
        ), { contextHeader: 'export' }, idSubRoutePeriodicity, type).pipe(takeUntil(ctx.stopped)).subscribe((response) => {
          get(response, 'data.data', []).map((item) => {
            ctx.processor.addToSection(title, this.transformItem(item));
          });
          ctx.next();
        });
      },
    }).start();
  }

  canStartTask(id) {
    if (this.exportService.taskIsActive(id)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }
}
